import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  User,
  Building2,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  Bell,
  Search,
  LogOut,
  CreditCard,
  Users,
  Calendar,
  AlertCircle,
  ShieldCheck
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useIsMobile } from '@/hooks/useMediaQuery'
import LicenseBanner from '@/components/LicenseBanner'
import GradientButton from '@/components/ui/GradientButton'
import api from '@/services/api'
import LanguageSwitcher from '@/components/LanguageSwitcher'

// ============================================================
// INTERFACES
// ============================================================

interface NewLead {
  id: string
  name: string
  phone: string
  created_at: string
}

interface Activity {
  id: string
  lead_id: string
  lead_name: string
  lead_phone: string
  activity_type: string
  title: string
  scheduled_at: string
  status: string
}

// ============================================================
// DEFINICIÓN CENTRALIZADA DEL MENÚ
// Fix #3: navigation es la única fuente de verdad para rutas y sección activa.
// getActiveSection() se deriva de este array — nunca duplicar strings de rutas.
//
// Para agregar un ítem al menú:
//   1. Agregar objeto aquí con { name, href, icon, id, locked }
//   2. Agregar la Route correspondiente en App.tsx
//   3. NO hay paso 3 — getActiveSection() funciona automáticamente
// ============================================================

export interface NavItem {
  name: string
  href: string
  icon: React.ElementType
  id: string
  locked: boolean | ((plan: string) => boolean)
}

export const buildNavigation = (planNormalized: string, nomCandidate = false): NavItem[] => [
  { name: 'Dashboard',        href: '/dashboard',            icon: LayoutDashboard, id: 'dashboard',  locked: false },
  { name: 'Mi Perfil',        href: '/dashboard/profile',    icon: User,            id: 'profile',    locked: false },
  { name: 'Propiedades',      href: '/dashboard/properties', icon: Building2,       id: 'properties', locked: false },
  { name: 'Leads CRM',        href: '/dashboard/leads',      icon: Users,           id: 'leads',      locked: planNormalized === 'starter' },
  { name: 'Documentos',       href: '/dashboard/documents',  icon: FileText,        id: 'documents',  locked: false },
  { name: 'Analytics',        href: '/dashboard/analytics',  icon: BarChart3,       id: 'analytics',  locked: planNormalized === 'starter' },
  ...(nomCandidate ? [{ name: 'Cumplimiento NOM', href: '/dashboard/nom', icon: ShieldCheck, id: 'nom', locked: false } as NavItem] : []),  
  { name: 'Facturación',      href: '/dashboard/billing',    icon: CreditCard,      id: 'billing',    locked: false },
  { name: 'Configuración',    href: '/dashboard/settings',   icon: Settings,        id: 'settings',   locked: false },
]

// ============================================================
// FIX #3: getActiveSection derivado del array navigation
// Nunca agregar strings de rutas manualmente aquí.
// ============================================================

const getActiveSection = (pathname: string, navigation: NavItem[]): string => {
  if (pathname === '/dashboard') return 'dashboard'
  const match = navigation.find(item =>
    item.href !== '/dashboard' && pathname.includes(item.href.replace('/dashboard', ''))
  )
  return match?.id ?? 'dashboard'
}

// ============================================================
// FIX #2: Hooks migrados a api service (axios con interceptors)
// Ya no usan fetch() ni localStorage.getItem('token') directamente.
// El token, manejo de 401 y redirección lo gestiona api.ts centralmente.
// ============================================================

const useNewLeads = () => {
  const [leads, setLeads] = useState<NewLead[]>([])
  const [count, setCount] = useState(0)

  useEffect(() => {
    const fetchNewLeads = async () => {
      try {
        const { data } = await api.get('/api/agents/leads?status=new&limit=5')
        setLeads(data.leads || [])
        setCount(data.total || 0)
      } catch (error) {
        console.error('Error fetching new leads:', error)
      }
    }

    fetchNewLeads()
    const interval = setInterval(fetchNewLeads, 30000)
    return () => clearInterval(interval)
  }, [])

  return { leads, count }
}

const useActivitiesToday = () => {
  const [activities, setActivities] = useState<Activity[]>([])
  const [count, setCount] = useState(0)

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const { data } = await api.get('/api/agents/activities/today')
        if (data.success) {
          setActivities(data.activities || [])
          setCount(data.count || 0)
        }
      } catch (error) {
        console.error('Error fetching today activities:', error)
      }
    }

    fetchActivities()
    const interval = setInterval(fetchActivities, 60000)
    return () => clearInterval(interval)
  }, [])

  return { activities, count }
}

const useActivitiesOverdue = () => {
  const [count, setCount] = useState(0)

  useEffect(() => {
    const fetchOverdue = async () => {
      try {
        const { data } = await api.get('/api/agents/activities/overdue')
        if (data.success) {
          setCount(data.count || 0)
        }
      } catch (error) {
        console.error('Error fetching overdue activities:', error)
      }
    }

    fetchOverdue()
    const interval = setInterval(fetchOverdue, 60000)
    return () => clearInterval(interval)
  }, [])

  return count
}

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================

interface DashboardLayoutProps {
  children: React.ReactNode
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { t } = useTranslation('nav')
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const { agent, logout } = useAuth()
  const planNormalized = agent?.plan?.toLowerCase() || 'free'
  const location = useLocation()
  const navigate = useNavigate()
  const notificationsRef = useRef<HTMLDivElement>(null)
  const navigation = buildNavigation(planNormalized, agent?.nom_candidate || false)  
  const activeSection = getActiveSection(location.pathname, navigation)
  const { leads: newLeads, count: newLeadsCount } = useNewLeads()
  const { activities: todayActivities, count: todayActivitiesCount } = useActivitiesToday()
  const overdueCount = useActivitiesOverdue()
  const totalNotifications = newLeadsCount + todayActivitiesCount + overdueCount

  useEffect(() => {
    setSidebarOpen(!isMobile)
  }, [isMobile])

  useEffect(() => {
    if (isMobile) setSidebarOpen(false)
  }, [location.pathname, isMobile])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (agent?.license?.isBlocked && location.pathname !== '/dashboard/billing') {
      navigate('/dashboard/billing')
    }
  }, [agent?.license?.isBlocked, location.pathname, navigate])

  const closeSidebar = () => {
    if (isMobile) setSidebarOpen(false)
  }

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })

  const getTimeAgo = (dateString: string) => {
    const diffMs = new Date().getTime() - new Date(dateString).getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    if (diffMins < 60) return t('timeAgoMin', { count: diffMins })
    if (diffHours < 24) return t('timeAgoHour', { count: diffHours })
    return t('timeAgoDay', { count: Math.floor(diffMs / 86400000) })
  }

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      call: '📞', whatsapp: '💬', email: '📧', visit: '🏠',
      meeting: '🤝', property_show: '🔑', follow_up: '📋', note: '📝'
    }
    return icons[type] ?? '📌'
  }

  const isOverdue = (scheduledAt: string) => new Date(scheduledAt) < new Date()

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950">
      {/* Overlay móvil */}
      <AnimatePresence>
        {isMobile && sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : (isMobile ? -256 : -192),
          width: isMobile ? 256 : (sidebarOpen ? 256 : 64)
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className={`fixed inset-y-0 left-0 z-50 ${isMobile ? 'w-64' : ''}`}
      >
        <div className="h-full w-full rounded-none border-r border-white/20 backdrop-blur-lg bg-white/10 dark:bg-gray-900/20 shadow-xl overflow-hidden flex flex-col">
          <div className="flex h-full flex-col min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-3">
                <img
                  src="https://raw.githubusercontent.com/ruxo-rod/logos/main/logo%20IA.jpg"
                  alt="InnovAgent"
                  className="h-8 w-8 rounded-lg shadow-md"
                />
                <AnimatePresence>
                  {(sidebarOpen || isMobile) && (
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="font-bold text-white"
                    >
                      InnovAgent
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
              {isMobile ? (
                <button onClick={closeSidebar} className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                  <X size={20} />
                </button>
              ) : (
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                  {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                </button>
              )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-2 p-4 overflow-y-auto">
              {navigation.map((item) => {
                const isLocked = typeof item.locked === 'function' ? item.locked(planNormalized) : item.locked
                return (
                  <motion.a
                    key={item.name}
                    href={isLocked ? '/dashboard/billing' : item.href}
                    onClick={closeSidebar}
                    className={`
                      group flex items-center rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200
                      ${isLocked
                        ? 'text-white/30 cursor-pointer hover:bg-white/5'
                        : activeSection === item.id
                          ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border border-blue-500/30'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }
                    `}
                    whileHover={{ x: (sidebarOpen || isMobile) ? 4 : 0 }}
                    whileTap={{ scale: 0.98 }}
                    title={isLocked ? t('lockedTooltip') : undefined}
                  >
                    <item.icon
                      className={`
                        flex-shrink-0 transition-colors duration-200
                        ${isLocked ? 'text-white/25' : activeSection === item.id ? 'text-blue-400' : 'text-white/60 group-hover:text-white'}
                        ${(sidebarOpen || isMobile) ? 'mr-3 h-5 w-5' : 'h-6 w-6'}
                      `}
                    />
                    <AnimatePresence>
                      {(sidebarOpen || isMobile) && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.2 }}
                          className="flex items-center gap-2 flex-1"
                        >
                          {t(item.id)}
                          {isLocked && (
                            <span className="ml-auto text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/30">
                              {t('lockedBadge')}
                            </span>

                          )}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.a>
                )
              })}
            </nav>

            {/* Profile Section */}
            <div className="border-t border-white/10 p-4">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {agent?.avatar_url ? (
                    <img src={agent.avatar_url} alt={agent.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                      <span className="text-xs font-semibold text-white">{agent?.name?.charAt(0)}</span>
                    </div>
                  )}
                </div>
                <AnimatePresence>
                  {(sidebarOpen || isMobile) && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="flex-1 min-w-0"
                    >
                      <p className="text-sm font-medium text-white truncate">{agent?.name}</p>  
                      <p className="text-xs text-white/60 truncate">{agent?.plan || t('freePlan')}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <AnimatePresence>
                {(sidebarOpen || isMobile) && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="mt-3"
                  >
                    <GradientButton size="sm" variant="secondary" className="w-full" onClick={logout}>
                      <LogOut size={16} />
                      {t('logout')}
                    </GradientButton>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

      </motion.div>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${isMobile ? 'ml-0' : sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        <div className="p-4 md:p-6">

          {/* Top Header */}
          <div className="mb-4 md:mb-6 flex items-center justify-between gap-2">
            <div className="flex items-center space-x-2 md:space-x-4">
              {(isMobile || !sidebarOpen) && (
                <button onClick={() => setSidebarOpen(true)} className="rounded-lg p-2 text-white/70 hover:bg-white/10 hover:text-white transition-colors">
                  <Menu size={20} />
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Search */}
              <div className="relative hidden md:block">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  placeholder={t('search')}
                  className="rounded-xl bg-white/10 border border-white/20 py-2 pl-10 pr-4 text-white placeholder-white/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 w-48 lg:w-64"
                />
              </div>

              {/* Language Switcher */}
              <LanguageSwitcher variant="compact" />

              {/* Notifications */}
              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setNotificationsOpen(!notificationsOpen)}
                  className="rounded-xl bg-white/10 border border-white/20 p-2 text-white/70 hover:bg-white/20 hover:text-white transition-colors"
                >
                  <Bell size={20} />
                  {totalNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-medium text-white">
                      {totalNotifications > 99 ? '99+' : totalNotifications}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {notificationsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-[calc(100vw-2rem)] md:w-96 max-w-[24rem] bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50"
                    >
                      <div className="px-4 py-3 border-b border-white/10 bg-white/5">
                        <h3 className="text-white font-semibold">{t('notifications')}</h3>
                      </div>

                      <div className="max-h-[60vh] md:max-h-96 overflow-y-auto">
                        {/* Leads Nuevos */}
                        <div className="border-b border-white/10">
                          <div className="px-4 py-2 bg-blue-500/10 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Users className="w-4 h-4 text-blue-400" />
                              <span className="text-blue-400 font-medium text-sm">{t('newLeads')}</span>
                            </div>
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">{newLeadsCount}</span>
                          </div>
                          {newLeads.length === 0 ? (
                            <div className="px-4 py-3 text-gray-400 text-sm text-center">{t('noNewLeads')}</div>
                          ) : (
                            <>
                              {newLeads.slice(0, 3).map((lead) => (
                                <div
                                  key={lead.id}
                                  onClick={() => { navigate('/dashboard/leads'); setNotificationsOpen(false) }}
                                  className="px-4 py-3 hover:bg-white/5 cursor-pointer flex items-center justify-between transition-colors"
                                >
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                      <User className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-white text-sm font-medium">{lead.name || t('noName')}</p>
                                      <p className="text-gray-400 text-xs">{getTimeAgo(lead.created_at)}</p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {newLeadsCount > 3 && (
                                <div
                                  onClick={() => { navigate('/dashboard/leads?status=new'); setNotificationsOpen(false) }}
                                  className="px-4 py-2 text-blue-400 text-sm text-center hover:bg-white/5 cursor-pointer transition-colors"
                                >
                                  {t('viewAllLeads', { count: newLeadsCount })}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Actividades Hoy */}
                        <div>
                          <div className="px-4 py-2 bg-amber-500/10 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4 text-amber-400" />
                              <span className="text-amber-400 font-medium text-sm">{t('todayActivities')}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              {overdueCount > 0 && (
                                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center space-x-1">
                                  <AlertCircle className="w-3 h-3" />
                                  <span>{overdueCount}</span>
                                </span>
                              )}
                              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{todayActivitiesCount}</span>
                            </div>
                          </div>
                          {todayActivities.length === 0 && overdueCount === 0 ? (
                            <div className="px-4 py-3 text-gray-400 text-sm text-center">{t('noTodayActivities')}</div>
                          ) : (
                            <>
                              {todayActivities.slice(0, 4).map((activity) => {
                                const overdue = isOverdue(activity.scheduled_at)
                                return (
                                  <div
                                    key={activity.id}
                                    onClick={() => { navigate('/dashboard/leads?tab=seguimiento'); setNotificationsOpen(false) }}
                                    className={`px-4 py-3 hover:bg-white/5 cursor-pointer flex items-center justify-between transition-colors ${overdue ? 'bg-red-500/5' : ''}`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-lg ${overdue ? 'bg-red-500/20' : 'bg-amber-500/20'}`}>
                                        {getActivityIcon(activity.activity_type)}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-white text-sm font-medium truncate">{activity.title}</p>
                                        <p className="text-gray-400 text-xs truncate">{activity.lead_name} • {formatTime(activity.scheduled_at)}</p>
                                      </div>
                                    </div>
                                    {overdue && <span className="text-red-400 text-xs flex-shrink-0">{t('overdue')}</span>}
                                  </div>
                                )
                              })}
                              {(todayActivitiesCount > 4 || overdueCount > 0) && (
                                <div
                                  onClick={() => { navigate('/dashboard/leads?tab=seguimiento'); setNotificationsOpen(false) }}
                                  className="px-4 py-2 text-amber-400 text-sm text-center hover:bg-white/5 cursor-pointer transition-colors"
                                >
                                  {t('viewAllActivities')}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <LicenseBanner license={agent?.license} />
            {children}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default DashboardLayout
